import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAlleNachrichten } from '../../hooks/buch'
import { useMembers, useTrip } from '../../hooks/queries'
import { Beitrag } from './Buchteile'
import { Druckknopf } from './Druckknopf'
import { dayLabel, tripPeriod } from '../../lib/format'
import { BackIcon } from '../../components/Icons'
import '../../styles/buch.css'

/**
 * Das private Gespräch mit einem Menschen, als eigenes Dokument (#14).
 *
 * **Warum je Person eines und nicht ein gemeinsames.** Ein Buch ist dazu da,
 * verschenkt zu werden — das ist sein Zweck. Ein einziges „privat"-Dokument
 * mit allen Gesprächen darin wäre genau das Leck: Annas Antworten landen bei
 * Bert, sobald die Datei weitergereicht wird. Je Person ist die Datei von sich
 * aus an eine Beziehung adressiert, und Dateiname und erste Seite sagen es.
 *
 * **Der Filter hier verengt und erweitert nie.** Der Server liefert zu
 * `?with=X` auch die Beiträge an alle, als Zusammenhang — im Buch stehen die
 * schon, und ein zweites Mal wären sie nur Umfang. Weggelassen wird also
 * etwas, das erlaubt wäre; hinzugefügt wird nichts. Der Zusammenhang bleibt
 * trotzdem: `reply_to` trägt das zitierte Stück mit.
 */
export function PrivatbuchPage() {
  const { tripId, userId } = useParams()
  const id = Number(tripId)
  const wer = Number(userId)

  const trip = useTrip(id)
  const empfaenger = useMembers(id, true)
  const stand = useAlleNachrichten(id, wer)

  const versatz = trip.data?.tz_offset_min ?? null
  const person = (empfaenger.data ?? []).find((m) => m.user.id === wer)
  const name = person?.user.display_name ?? 'dieser Person'

  const briefe = stand.nachrichten.filter((m) => m.kind !== 'broadcast')

  // Der Fenstertitel wird im Druckdialog zum Dateinamen. Er nennt die Person,
  // damit niemand das falsche Dokument weiterreicht.
  useEffect(() => {
    const alt = document.title
    if (trip.data !== undefined) document.title = `${trip.data.title} — privat mit ${name}`
    return () => {
      document.title = alt
    }
  }, [trip.data, name])

  return (
    <div className="buch">
      <div className="buch__leiste buch__nurbildschirm">
        <Link className="icon-button" to={`/t/${id}/info`} aria-label="Zurück zur Reise">
          <BackIcon />
        </Link>
        <span className="bar__grow">
          {stand.laeuft ? `Wird zusammengestellt … (${stand.seiten} Seiten)` : `${briefe.length} Nachrichten`}
        </span>
        {stand.fehler !== null && (
          <p className="notice" role="alert" style={{ margin: 0 }}>
            {stand.fehler}
          </p>
        )}
        <Druckknopf
          bereit={!stand.laeuft && stand.fehler === null}
          wartetext={stand.laeuft ? 'Noch nicht fertig …' : 'Nicht bereit'}
          drucktext="Drucken oder als PDF sichern"
        />
      </div>

      <section className="buch__deckel">
        <h1>{trip.data?.title ?? 'Reise'}</h1>
        <p>{tripPeriod(trip.data?.starts_on ?? null, trip.data?.ends_on ?? null)}</p>
        <p>
          <strong>Privates Gespräch mit {name}</strong>
        </p>
        <p className="buch__warnung">
          Dieses Dokument ist <strong>nicht</strong> der Reisebericht. Es enthält
          den Briefwechsel zwischen dir und {name} — ihre Antworten und deine
          privaten Nachrichten an sie. Niemand sonst hat sie je gesehen. Wenn du
          etwas weitergeben möchtest, gib das Buch weiter, nicht dieses Blatt.
        </p>
      </section>

      <section className="buch__abschnitt">
        <h2>Der Briefwechsel</h2>
        {briefe.length === 0 && <p>Zwischen euch steht noch nichts.</p>}
        {briefe.map((nachricht, i) => {
          const vorher = briefe[i - 1]
          const neuerTag =
            vorher === undefined || dayLabel(vorher.created_at) !== dayLabel(nachricht.created_at)
          return (
            <div key={nachricht.id}>
              {neuerTag && <p className="buch__tag">{dayLabel(nachricht.created_at)}</p>}
              <p className="buch__zeit">
                {nachricht.kind === 'direct' ? 'Du' : nachricht.author.display_name}
              </p>
              {nachricht.reply_to !== null && (
                <p className="buch__zitat">„{nachricht.reply_to.body}"</p>
              )}
              <Beitrag nachricht={nachricht} versatz={versatz} />
            </div>
          )
        })}
      </section>
    </div>
  )
}
